import type { StorageRoom } from '../services/storageRoom.service';
import { formatUserName } from './name.util';
import { formatEntityDisplayName } from './softBreakIdentifier';

/** この件数超で保管庫検索欄を出す（以下は select だけで足りる） */
export const STORAGE_SEARCH_MIN_ROOMS = 8;

export function roomMatchesStorageSearch(
  room: StorageRoom,
  searchLower: string,
  language: string
): boolean {
  if (!searchLower) return true;
  const registrantName = room.user
    ? formatUserName(room.user.firstName, room.user.lastName, language)
    : '';
  return (
    room.name.toLowerCase().includes(searchLower) ||
    Boolean(room.location?.toLowerCase().includes(searchLower)) ||
    registrantName.toLowerCase().includes(searchLower)
  );
}

type SelectOptionRoom = Pick<StorageRoom, 'id' | 'name' | 'location' | 'user'>;

function ownerLabelForRoom(room: SelectOptionRoom, language: string): string {
  if (!room.user) return '';
  return formatUserName(room.user.firstName, room.user.lastName, language).trim();
}

/**
 * select 用ラベル一覧。
 * 一意な名前: 一覧に所有者が複数いるときだけ `名前 · 所有者`（保管オープンと揃える）。
 * 同名: 場所で区別し、場所も同じなら所有者 → なければ #id。
 */
export function buildStorageRoomSelectLabels(
  rooms: ReadonlyArray<SelectOptionRoom>,
  language = 'ja'
): Map<number, string> {
  const byName = new Map<string, SelectOptionRoom[]>();
  for (const room of rooms) {
    const group = byName.get(room.name);
    if (group) group.push(room);
    else byName.set(room.name, [room]);
  }

  const distinctOwners = new Set(
    rooms.map((r) => r.user?.id).filter((id): id is number => id != null)
  );
  const multiOwnerList = distinctOwners.size > 1;

  const labels = new Map<number, string>();
  for (const [name, group] of byName) {
    if (group.length === 1) {
      const alone = group[0];
      if (multiOwnerList) {
        const owner = ownerLabelForRoom(alone, language);
        labels.set(alone.id, owner ? `${name} · ${owner}` : name);
      } else {
        labels.set(alone.id, name);
      }
      continue;
    }

    const byLocation = new Map<string, SelectOptionRoom[]>();
    for (const room of group) {
      const key = room.location ?? '';
      const peers = byLocation.get(key);
      if (peers) peers.push(room);
      else byLocation.set(key, [room]);
    }

    for (const room of group) {
      const loc = room.location?.trim();
      const peers = byLocation.get(room.location ?? '') ?? [];
      if (peers.length > 1) {
        const owner = ownerLabelForRoom(room, language);
        if (owner) {
          labels.set(room.id, `${name} · ${owner}`);
        } else {
          labels.set(room.id, loc ? `${name} · ${loc} · #${room.id}` : `${name} · #${room.id}`);
        }
      } else if (loc) {
        labels.set(room.id, `${name} · ${loc}`);
      } else {
        const owner = ownerLabelForRoom(room, language);
        labels.set(room.id, owner ? `${name} · ${owner}` : `${name} · #${room.id}`);
      }
    }
  }
  return labels;
}

/** select の option 用（labels 未指定時は短い名前のみ） */
export function formatStorageRoomSelectOption(
  room: SelectOptionRoom,
  labels?: ReadonlyMap<number, string>,
  t?: Parameters<typeof formatEntityDisplayName>[1]
): string {
  return formatEntityDisplayName(labels?.get(room.id) ?? room.name, t);
}

/** title / 補助行用のフル表示 */
export function formatStorageRoomOptionLabel(
  room: Pick<StorageRoom, 'name' | 'location'>,
  t?: Parameters<typeof formatEntityDisplayName>[1]
): string {
  const name = formatEntityDisplayName(room.name, t);
  return room.location ? `${name} · ${room.location}` : name;
}

/** ネイティブ select option の表示長上限（全文は title / 補助行） */
export const STORAGE_SELECT_OPTION_MAX_CHARS = 48;

/** select option 用に長すぎるラベルを省略（コピー用の title は呼び出し側でフルを渡す） */
export function truncateForSelectOption(
  label: string,
  max = STORAGE_SELECT_OPTION_MAX_CHARS
): string {
  const text = label.trim();
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(1, max - 1))}…`;
}

/**
 * select 下の補助行（option と名前が重複しない）。
 * 場所があれば場所のみ。長い名前のときだけ全文。
 */
export function formatStorageRoomSelectedHint(
  room: Pick<StorageRoom, 'name' | 'location'>,
  t?: Parameters<typeof formatEntityDisplayName>[1]
): string | null {
  if (room.location) return room.location;
  const name = formatEntityDisplayName(room.name, t);
  return name.length >= 28 ? name : null;
}

/** 選択中ラベルを select 下に出すか */
export function shouldShowStorageRoomFullLabel(
  room: Pick<StorageRoom, 'name' | 'location'> | null | undefined,
  t?: Parameters<typeof formatEntityDisplayName>[1]
): boolean {
  if (!room) return false;
  return formatStorageRoomSelectedHint(room, t) != null;
}

/** 検索結果に選択中ルームが無いとき先頭へ戻す */
export function filterStorageRoomsForSelect(
  rooms: ReadonlyArray<StorageRoom>,
  storageSearch: string,
  selectedStorageRoomId: number | undefined,
  language: string
): StorageRoom[] {
  const searchLower = storageSearch.trim().toLowerCase();
  const filtered = rooms.filter((room) =>
    roomMatchesStorageSearch(room, searchLower, language)
  );
  if (
    selectedStorageRoomId &&
    !filtered.some((room) => room.id === selectedStorageRoomId)
  ) {
    const selected = rooms.find((room) => room.id === selectedStorageRoomId);
    if (selected) return [selected, ...filtered];
  }
  return filtered;
}
